import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { format, startOfWeek, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FileXls, EnvelopeSimple, CalendarBlank, Clock } from '@phosphor-icons/react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { user } = useAuth();
  const [reportType, setReportType] = useState('weekly');
  const [weekDate, setWeekDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (reportType === 'weekly') {
        const weekStart = format(weekDate, 'yyyy-MM-dd');
        response = await axios.get(`${API_URL}/api/reports/weekly?week_start=${weekStart}`, { withCredentials: true });
      } else {
        response = await axios.get(`${API_URL}/api/reports/monthly?year=${year}&month=${month}`, { withCredentials: true });
      }
      setEntries(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  }, [reportType, weekDate, year, month, API_URL]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExportExcel = async () => {
    try {
      let url;
      let filename;
      if (reportType === 'weekly') {
        const weekStart = format(weekDate, 'yyyy-MM-dd');
        url = `${API_URL}/api/reports/weekly/excel?week_start=${weekStart}`;
        filename = `rapport_semaine_${weekStart}.xlsx`;
      } else {
        url = `${API_URL}/api/reports/monthly/excel?year=${year}&month=${month}`;
        filename = `rapport_${year}_${month}.xlsx`;
      }

      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      toast.success('Export Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleSendEmails = async () => {
    if (user?.role !== 'admin') return;
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/send-weekly-reports`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const totalHours = entries.reduce((sum, e) => sum + e.total_hours, 0);

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <h1 className="page-title">Rapports</h1>
        <p className="page-subtitle">Exportez vos données en Excel</p>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${reportType === 'weekly' ? 'active' : ''}`}
          onClick={() => setReportType('weekly')}
          data-testid="tab-weekly"
        >
          Hebdomadaire
        </button>
        <button 
          className={`tab ${reportType === 'monthly' ? 'active' : ''}`}
          onClick={() => setReportType('monthly')}
          data-testid="tab-monthly"
        >
          Mensuel
        </button>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {reportType === 'weekly' ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Semaine du</label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="week-picker-trigger"
                      className="form-input"
                      style={{ 
                        width: '200px',
                        textAlign: 'left', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      {format(weekDate, 'dd/MM/yyyy')}
                      <CalendarBlank size={20} color="#5E5F66" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={weekDate}
                      onSelect={(d) => { 
                        if (d) { 
                          setWeekDate(startOfWeek(d, { weekStartsOn: 1 })); 
                          setCalendarOpen(false); 
                        } 
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Mois</label>
                  <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                    <SelectTrigger data-testid="month-select" style={{ width: '160px' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Année</label>
                  <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                    <SelectTrigger data-testid="year-select" style={{ width: '120px' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="export-buttons" style={{ marginLeft: 'auto' }}>
              <button
                data-testid="export-excel-btn"
                className="btn btn-primary"
                onClick={handleExportExcel}
              >
                <FileXls size={18} />
                Exporter Excel
              </button>
              {user?.role === 'admin' && (
                <button
                  data-testid="send-emails-btn"
                  className="btn btn-secondary"
                  onClick={handleSendEmails}
                  disabled={sendingEmail}
                >
                  <EnvelopeSimple size={18} />
                  {sendingEmail ? 'Envoi...' : 'Envoyer par email'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ 
        backgroundColor: '#F4F5F7', 
        padding: '16px 24px', 
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid #E2E2E6'
      }}>
        <span className="label-text">Total heures</span>
        <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#002FA7' }}>
          {Math.round(totalHours * 100) / 100}h
        </span>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner" />
        </div>
      ) : entries.length > 0 ? (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                {user?.role === 'admin' && <th>Employé</th>}
                <th>Date</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Pause</th>
                <th>Total</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  {user?.role === 'admin' && <td>{entry.user_name}</td>}
                  <td>{format(parseISO(entry.date), 'EEEE dd/MM', { locale: fr })}</td>
                  <td>{entry.start_time}</td>
                  <td>{entry.end_time}</td>
                  <td>{entry.break_duration} min</td>
                  <td style={{ fontWeight: '600' }}>{Math.round(entry.total_hours * 100) / 100}h</td>
                  <td>
                    <span className={`status-badge ${entry.status}`}>
                      {entry.status === 'approved' ? 'Validé' : entry.status === 'rejected' ? 'Rejeté' : 'En attente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Clock size={48} className="empty-state-icon" />
          <p className="empty-state-text">Aucune donnée pour cette période</p>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
